import { Component, NgZone } from '@angular/core';
import { Location } from '@angular/common';

import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { environment } from '../environments/environment';
import { LoginService } from './services/login.service';
import { BrandService } from '../services/brand-config.service';
import { SessionService } from './services/session.service';
import { RoutineService } from './services/routine.service';
import { RoutineReminderService } from './services/routine-reminder.service';
import { TOOL_BY_ID } from './shell/shell.models';
import { AuthService } from './services/auth.service';
import { MatSnackBar } from '@angular/material/snack-bar';
import { CommonModule } from '@angular/common';
import { RtlService } from './services/rtl.service';
import { TranslationService } from './services/translation.service';
import { LanguageNavigationService } from './services/language-navigation.service';
import { filter, take } from 'rxjs';
import { GtmAnalyticsService } from './services/gtm-analytics.service';
import { PlatformService } from './services/platform.service';
import { WidgetPinService } from './services/widget-pin.service';
import { WellnessWidgetService } from './services/wellness-widget.service';
import { WidgetInstructionsComponent } from './widget-instructions/widget-instructions.component';
import { App as CapApp } from '@capacitor/app';
import { Browser } from '@capacitor/browser';
import { MatDialog } from '@angular/material/dialog';
import { ErrorReportingService } from './services/error-reporting.service';
import { KeyboardInsetService } from './services/keyboard-inset.service';
import { MappingTelemetryService } from './services/mapping-telemetry.service';

import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { CookieConsentComponent } from './cookie-consent/cookie-consent.component';

@Component({
  selector: 'app-root',
  standalone: true,
  imports: [RouterOutlet, CommonModule, MatProgressSpinnerModule, CookieConsentComponent],
  providers: [],
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  message = '';
  entities: any[] = [];
  env = environment.env_name;

  private lastAuthUserId: string | null = null;
  isLoginOrSignupPage = false;
  clerkLocalizationLang: string;
  isTranslationLoaded$ = this.translationService.isInitialized$;

  constructor(
    private loginService: LoginService,
    private brandService: BrandService,
    private snackBar: MatSnackBar,
    public sessionService: SessionService,
    public authService: AuthService,
    public rtlService: RtlService,
    private router: Router,
    public translationService: TranslationService,
    private languageNavigationService: LanguageNavigationService,
    private gtmAnalytics: GtmAnalyticsService,
    private platform: PlatformService,
    private location: Location,
    private dialog: MatDialog,
    private widgetPin: WidgetPinService,
    private wellnessWidget: WellnessWidgetService,
    private reporter: ErrorReportingService,
    private keyboardInset: KeyboardInsetService,
    private mappingTelemetry: MappingTelemetryService,
    private routineSvc: RoutineService,
    private routineReminders: RoutineReminderService,
    private zone: NgZone
  ) {
    this.languageNavigationService.patchRouterForLanguageSupport();
    this.message = this.translationService.translate('miscUi.loading');
    this.initLang();
    this.wireAuth();
    this.trackRouteChanges();
    this.wireNativeBackButton();
    this.wireNativeAppUrlOpen();
    this.wireNotificationTaps();
    this.wireWidgetRefresh();
    this.handleOrphanAuthCode();
    // one keyboard measurement for the whole app, published as --kb-inset and
    // body.kb-open (correction brief 5)
    this.keyboardInset.start();
    // internal mapping analytics: the four timers + the named event model
    // (correction brief 9, 43, 44). Nothing it measures reaches a screen.
    this.mappingTelemetry.start();
  }

  // A ?code= we can't exchange means an auth email link (signup confirmation)
  // was opened in a DIFFERENT browser/app than the one that started the flow —
  // the PKCE verifier lives in that browser's localStorage, so supabase-js's
  // detectSessionInUrl exchange silently fails and the user is dumped into the
  // guest funnel as if nothing happened. But GoTrue already confirmed the email
  // server-side before redirecting, so the honest UX is: "you're confirmed,
  // just sign in" → route to login instead of the funnel.
  private handleOrphanAuthCode(): void {
    if (this.platform.isNative) return;
    let code: string | null = null;
    try {
      code = new URLSearchParams(window.location.search).get('code');
    } catch {}
    if (!code) return;
    const path = window.location.pathname.toLowerCase();
    // These routes run their own link handling.
    if (path.includes('confirm-email') || path.includes('reset-password')) return;
    if (this.isDoctorPortalRoute()) return;
    this.authService.waitUntilReady().then(async () => {
      if (this.authService.session$.value) return; // exchanged fine (same browser)
      this.reporter.logEvent('auth.orphan-code', {});
      // i18next may still be fetching the locale JSON this early in boot —
      // translate only after init or the snackbar shows the raw key.
      const lang = await this.translationService.getCurrentLanguageAsync();
      this.snackBar.open(
        this.translationService.translate('auth.confirmEmail.confirmedPleaseLogin'),
        'OK',
        { duration: 8000 },
      );
      this.router.navigate([`/${lang}/login`]);
    });
  }

  // Repaint the home-screen widget when the app resumes (steps/Health may have
  // moved while backgrounded) and once on launch.
  private wireWidgetRefresh(): void {
    if (!this.platform.isNative) return;
    this.wellnessWidget.refresh();
    CapApp.addListener('appStateChange', (state) => {
      if (state?.isActive) this.wellnessWidget.refresh();
    });
    // Repaint with localized labels when the user switches app language.
    this.translationService.currentLanguage$.subscribe(() =>
      this.wellnessWidget.refresh()
    );
  }

  private lastProcessedAuthCode: string | null = null;

  private wireNativeAppUrlOpen(): void {
    if (!this.platform.isNative) return;
    // Cold-start widget and auth links can arrive before the listener exists.
    CapApp.getLaunchUrl()
      .then((r) => {
        if (r?.url) void this.handleNativeAppUrl(r.url);
      })
      .catch(() => {});
    CapApp.addListener('appUrlOpen', async (event) => {
      if (event?.url) await this.handleNativeAppUrl(event.url);
    });
  }

  private async handleNativeAppUrl(raw: string): Promise<void> {
    if (this.openWidgetLink(raw)) return;
    try {
        const url = new URL(raw);
        // Supabase's DEFAULT recovery/confirm templates hit the /auth/v1/verify
        // endpoint, which redirects back with the session in the URL *fragment*
        // (#access_token=…&type=recovery, implicit flow) — not the query string.
        // Reading only searchParams meant the reset link "did nothing" (no code /
        // token_hash found → early return). Parse the fragment too so the link
        // works whether the email uses the token_hash template or the default one.
        const hashParams = new URLSearchParams((url.hash || '').replace(/^#/, ''));
        const code = url.searchParams.get('code');
        const tokenHash = url.searchParams.get('token_hash');
        const accessToken = hashParams.get('access_token');
        const refreshToken = hashParams.get('refresh_token');
        const type = url.searchParams.get('type') || hashParams.get('type');
        // Not an auth deep link → nothing to do.
        if (!code && !tokenHash && !accessToken) return;
        this.reporter.logEvent('auth.link.received', { kind: tokenHash ? 'token_hash' : accessToken ? 'fragment' : 'code', type });
        const dedupeKey = code || tokenHash || accessToken!;
        if (this.lastProcessedAuthCode === dedupeKey) return;
        this.lastProcessedAuthCode = dedupeKey;
        Browser.close().catch(() => {});
        // Recovery deep link (brand://reset-password?…): flag + route to the
        // reset form BEFORE the session emits, so wireAuth doesn't auto-land it.
        const isReset = /reset-password/i.test(raw) || type === 'recovery';
        if (isReset) {
          this.resettingPassword = true;
          const lang = this.clerkLocalizationLang || 'en';
          this.router.navigate([`/${lang}/reset-password`]);
        }
        // Both confirm-signup and recovery emails now use the token_hash template
        // (`?token_hash=&type=`), verified via verifyOtp — no PKCE verifier needed,
        // so it also works when the link opens in the mail app's own webview.
        // Legacy `?code=` (older/social links) still exchanged as before.
        const { error } = tokenHash
          ? await this.authService.verifyOtp(
              tokenHash,
              type === 'recovery' ? 'recovery' : type === 'email' ? 'email' : 'signup',
            )
          : accessToken && refreshToken
          ? await this.authService.setSession(accessToken, refreshToken)
          : await this.authService.exchangeCodeForSession(code!);
        if (error) {
          // Suppress error if user already has a session (likely duplicate exchange)
          const hasSession = !!this.authService.session$.value;
          if (!hasSession) {
            console.error('native auth link failed', error);
            this.reporter.report(
              'native auth link failed: ' + (error?.message || ''),
              'auth-landing'
            );
            this.snackBar.open(
              this.translationService.translate('miscUi.signInFailed'),
              'OK',
              { duration: 5000 }
            );
          }
          return;
        }
        // Signup confirmation (not reset): hand to the loading spinner so it lands
        // home / on the stashed results deep-link, mirroring web ConfirmEmailComponent.
        if (!isReset) {
          const lang = this.clerkLocalizationLang || 'en';
          this.router.navigate([`/${lang}/app/loading`]);
        }
    } catch (e) {
      console.error('appUrlOpen handler failed', e);
      this.reporter.report(e, 'auth-landing');
    }
  }

  // A widget tile tap opens markable://widget/<metric>. Map the metric to the
  // tool that moves it. Cold start: the boot navigation has to settle first;
  // warm: navigate now.
  private static readonly WIDGET_TOOL: Record<string, string> = {
    water: 'water',
    protein: 'meal',
    carbs: 'meal',
    fat: 'meal',
    fiber: 'meal',
    calories: 'meal',
    steps: 'steps',
    checkin: 'diary',
    sleep: 'sleep',
  };
  private openWidgetLink(raw: string): boolean {
    const m = /^[a-z]+:\/\/widget\/?([a-z]*)/i.exec(raw);
    if (!m) return false;
    this.openTool(AppComponent.WIDGET_TOOL[m[1]]);
    return true;
  }

  // A tap on a routine reminder (or a steps milestone) opens the tool it is
  // about: the notification carries extra.toolId. The plugin retains the tap
  // event until this listener registers, so a cold start is covered too.
  private wireNotificationTaps(): void {
    if (!this.platform.isNative) return;
    void (async () => {
      try {
        const { LocalNotifications } = await import('@capacitor/local-notifications');
        await LocalNotifications.addListener('localNotificationActionPerformed', (a) => {
          const toolId = a?.notification?.extra?.toolId;
          this.reporter.logEvent('reminders.tap', { toolId: toolId || null });
          this.openTool(TOOL_BY_ID[toolId] ? toolId : undefined);
        });
      } catch (e) {
        console.error('notification tap wiring failed:', e);
      }
    })();
  }

  /** Open a tool screen (home when unknown). Cold start waits for the boot
   *  navigation so the tool lands on top of home and Back returns there. */
  private openTool(tool?: string): void {
    const lang = this.translationService.getCurrentLanguage() || 'en';
    const path = tool ? `/${lang}/app/tool/${tool}` : `/${lang}/app/home`;
    if (!this.sessionService.session?.user) return;
    const go = () => this.zone.run(() => this.router.navigateByUrl(path));
    if (this.router.navigated) {
      go();
    } else {
      // cold start: let the boot navigation (cached home) settle, then open the
      // tool on top so Back returns home. landAfterAuth leaves a tool URL alone.
      this.router.events
        .pipe(filter((e) => e instanceof NavigationEnd), take(1))
        .subscribe(() => setTimeout(go, 0));
    }
  }

  private lastBackPress = 0;
  private static readonly HOME_URL_PATTERN =
    /^\/(?:[a-z]{2,5}\/)?(?:app\/)?(?:welcome|new-test|home)?\/?(?:\?.*)?$/i;

  /** Re-lay her routine reminders on the OS at every launch, so the schedule
   *  she set in the profile keeps firing even after a reinstall or an OS purge.
   *  No-ops on web and when she has no active routine. */
  private syncRoutineReminders(): void {
    if (!this.platform.isNative) return;
    void (async () => {
      try {
        const schedule = await this.routineSvc.getSchedule();
        await this.routineReminders.sync(schedule);
      } catch (e) {
        console.error('routine reminders: launch sync failed:', e);
      }
    })();
  }

  private wireNativeBackButton(): void {
    if (!this.platform.isNative) return;
    CapApp.addListener('backButton', () => {
      this.platform.runInZone(() => this.handleBack());
    });
  }

  private handleBack(): void {
    const openDialogs = this.dialog.openDialogs;
    if (openDialogs.length > 0) {
      openDialogs[openDialogs.length - 1].close();
      return;
    }

    const atHome = AppComponent.HOME_URL_PATTERN.test(this.router.url);
    if (!atHome) {
      this.location.back();
      return;
    }

    const now = Date.now();
    if (now - this.lastBackPress < 2000) {
      CapApp.exitApp();
      return;
    }
    this.lastBackPress = now;
    this.snackBar.open(
      this.translationService.translate('miscUi.tapBackToExit'),
      'Close',
      { duration: 2000 }
    );
  }

  private trackRouteChanges(): void {
    this.router.events
      .pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.isLoginOrSignupPage =
          event.url.includes('/login') || event.url.includes('/signup');

        this.gtmAnalytics.trackPageView(
          window.location.pathname,
          document.title
        );
      });
  }

  private async initLang(): Promise<void> {
    this.clerkLocalizationLang =
      await this.translationService.getCurrentLanguageAsync();
  }

  private resettingPassword = false;

  /** ResetPasswordComponent calls this once the new password is saved so the
   *  subsequent real sign-in is no longer treated as a recovery landing. */
  clearPasswordReset(): void {
    this.resettingPassword = false;
  }

  // A recovery-email session is a *temporary* auth used only to set a new
  // password — it must NOT trigger the normal "build app session + land on
  // home" flow, or the user "just gets logged in" with no chance to reset.
  // PKCE recovery emits SIGNED_IN (not PASSWORD_RECOVERY), so the reliable
  // signal is the redirect path, not the auth event.
  private isPasswordResetRoute(): boolean {
    return (
      this.resettingPassword ||
      window.location.pathname.toLowerCase().includes('/reset-password') ||
      // Native signup confirmed in the mail browser: the session is for the
      // app (handed over via the deep link), so don't land the web app here.
      (!this.platform.isNative &&
        window.location.pathname.toLowerCase().includes('/confirm-email-app'))
    );
  }

  private wireAuth(): void {
    this.authService.session$.subscribe(async (session) => {
      if (this.isDoctorPortalRoute()) {
        return;
      }
      if (this.isPasswordResetRoute()) {
        return;
      }
      const userId = session?.user?.id ?? null;
      if (!userId) {
        this.lastAuthUserId = null;
        return;
      }
      if (this.lastAuthUserId === userId) return;
      this.lastAuthUserId = userId;
      // back from the provider: the clock she is on resumes, and section 43
      // gets its auth_completed. The mapping itself is unchanged by this.
      this.mappingTelemetry.resume('oauth');
      this.mappingTelemetry.track('auth_completed');
      await this.handleSupabaseSession(session.access_token);
    });
  }

  private async handleSupabaseSession(accessToken: string): Promise<void> {
    // Doctor portal drives its OWN login + routing: MaccabiPortalComponent calls
    // loginService.handleLogin and then navigates to the patient list (or the
    // email-2FA gate) itself. The global auth handler must NOT interfere here -
    // its fresh-login "/app/loading" hop below would yank a registering /
    // logging-in doctor off /app/doctor to the consumer funnel (new-test) via
    // the spinner, before landAfterAuth's isDoctorPortalRoute guard can fire.
    // Leave the portal to run its own flow.
    if (this.isDoctorPortalRoute()) return;
    // A cached app session means this is a returning user: the guard already
    // painted her real home from the persisted snapshot. Refresh her app session
    // in the background and NEVER flash the "setting up your account" loader.
    const hadSession = !!this.sessionService.session?.token;
    this.reporter.logEvent('auth.login.start', { returning: hadSession });
    try {
      const lang = this.clerkLocalizationLang || 'en';
      if (!hadSession) {
        this.router.navigate([`/${lang}/app/loading`]);
      }
      await this.loginService.handleLogin(
        accessToken,
        this.brandService.config.brandName,
        sessionStorage.getItem('purpose')
      );
      this.reporter.logEvent('auth.login.ok', { returning: hadSession });
      this.landAfterAuth();
      this.syncRoutineReminders();
      this.maybePromptWidgetPin();
      this.maybePromptWidgetInstructions();
    } catch (error) {
      console.error('Error in login', error);
      this.reporter.report(error, 'auth-landing');
      if (this.isDoctorPortalRoute()) {
        return;
      }
      // Returning user + transient refresh failure (offline / server blip):
      // keep her on the cached home instead of nuking the session and bouncing
      // to login. The Supabase token was already valid (it just emitted), and a
      // genuinely revoked session self-heals via the 401 auth-error handler on
      // the next real API call. The next app resume retries the refresh.
      if (hadSession) {
        return;
      }
      try {
        await this.authService.signOut();
      } catch {}
      this.snackBar.open(
        this.translationService.translate('miscUi.errorInLogin'),
        'OK',
        {
          duration: 5000,
        }
      );
      this.router.navigate(['/login']);
    }
  }

  // After (re)login, land on the shell: HOME, or onboarding if not done.
  // Only from entry routes (welcome/new-test/loading/login/signup/root) so we
  // never yank a user off a deep link; a stored redirectUrl (guestGuard) wins.
  private landAfterAuth(): void {
    if (this.isDoctorPortalRoute()) return;
    if (this.sessionService.getRedirectUrl()) return;
    // A signup deep link is pending in user metadata (cross-device email
    // confirmation) — the loading spinner lands it; don't yank her to home.
    if (this.authService.getSignupRedirect()) return;
    const full = this.router.url.toLowerCase();
    if (full.includes('scanid=')) return; // mid-scan reload — leave in place
    const url = full.split('?')[0];
    const isEntry =
      /\/app\/(welcome|new-test|loading)\/?$/.test(url) ||
      /\/(login|signup)(\/|$)/.test(url) ||
      /\/app\/?$/.test(url) ||
      /^\/(en|he)\/?$/.test(url) ||
      url === '/' ||
      url === '';
    if (!isEntry) return;
    const lang =
      this.clerkLocalizationLang ||
      this.translationService.getCurrentLanguage() ||
      'en';
    // Spec §3: the app has no separate onboarding — the woman already completed
    // the web flow. After auth she lands straight on HOME (continuation, not a
    // fresh start).
    this.reporter.logEvent('auth.land', { branch: 'app-home' });
    this.router.navigate([`/${lang}/app/home`]);
  }

  private isDoctorPortalRoute(): boolean {
    return /^\/(?:en|he)\/(?:app\/doctor|doctor|maccabi)(?:\/|$)/.test(window.location.pathname.toLowerCase());
  }

  private async maybePromptWidgetPin(): Promise<void> {
    if (!this.platform.isAndroid) return;
    try {
      if (await this.widgetPin.hasWidget()) return;
      if (!(await this.widgetPin.isSupported())) return;
      const key = 'widget-pin-prompt:v2';
      if (localStorage.getItem(key)) return;
      await this.widgetPin.requestPin();
      localStorage.setItem(key, '1');
    } catch (e) {
      console.warn('widget pin prompt failed', e);
    }
  }

  // iOS has no API to pin a widget programmatically, so show one-time manual
  // instructions instead of the Android system pin dialog.
  private maybePromptWidgetInstructions(): void {
    if (!this.platform.isIOS) return;
    try {
      const key = 'widget-instructions-prompt:v1';
      if (localStorage.getItem(key)) return;
      localStorage.setItem(key, '1');
      this.dialog.open(WidgetInstructionsComponent, {
        width: '400px',
        maxWidth: '92vw',
        panelClass: 'custom-dialog-class',
      });
    } catch (e) {
      console.warn('widget instructions prompt failed', e);
    }
  }
}
